import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Mail, Phone, Crown, Star } from 'lucide-react';

const CoupleBanner = () => {
  return (
    <section className="py-24 px-6 relative overflow-hidden bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50" data-testid="couple-banner">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            rotate: 360,
            scale: [1, 1.2, 1]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute -top-32 -right-32 w-96 h-96 bg-gradient-to-br from-rose-200/30 to-pink-300/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            rotate: -360,
            scale: [1, 1.3, 1]
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute -bottom-32 -left-32 w-96 h-96 bg-gradient-to-tr from-purple-200/30 to-pink-300/20 rounded-full blur-3xl"
        />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-rose-200 shadow-lg"
            >
              <Star className="w-4 h-4 text-rose-500 fill-rose-500" />
              <span className="text-sm font-semibold text-rose-600">Exclusive Experience</span>
            </motion.div>

            {/* Title */}
            <div className="space-y-4">
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-rose-600 via-pink-600 to-purple-600 leading-tight" data-testid="couple-banner-title">
                Be the Bride & Groom
              </h2>
              <div className="flex items-center gap-3">
                <div className="h-1 w-20 bg-gradient-to-r from-rose-500 to-pink-500 rounded-full"></div>
                <Crown className="w-6 h-6 text-rose-500" />
              </div>
            </div>

            {/* Description */}
            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed font-light" data-testid="couple-banner-description">
              Live the dream of a <span className="font-semibold text-rose-600">traditional Kerala wedding</span> without the stress. Be celebrated, pampered, and create unforgettable memories.
            </p>

            {/* Features list */}
            <div className="space-y-3">
              {[
                'Full traditional wedding ceremony',
                'Professional photography & videography',
                'Authentic Kerala rituals & customs',
                'No planning stress, just pure joy'
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-rose-500 to-pink-500"></div>
                  <span className="text-gray-700 font-medium">{feature}</span>
                </motion.div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <a
                href="mailto:events@thaalikettu.com"
                className="group relative px-8 py-4 bg-gradient-to-r from-rose-500 to-pink-600 text-white rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 overflow-hidden"
                data-testid="couple-banner-email"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  <Mail className="w-5 h-5" />
                  Get Started
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-rose-600 to-pink-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </a>

              <a
                href="tel:+919074500956"
                className="group px-8 py-4 bg-white/90 backdrop-blur-sm text-rose-600 rounded-full font-semibold text-lg border-2 border-rose-300 hover:border-rose-500 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Call Us Now
              </a>
            </div>

            {/* Availability notice */}
            <p className="text-sm text-gray-600 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
              Limited spots available - Book your date today!
            </p>
          </motion.div>

          {/* Right side - Visual element */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative">
              {/* Main card */}
              <div className="relative bg-gradient-to-br from-white to-rose-50/50 rounded-3xl p-12 shadow-2xl border border-rose-100/50 backdrop-blur-sm">
                {/* Floating crowns */}
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -top-6 -left-6 bg-gradient-to-br from-rose-500 to-pink-600 p-4 rounded-2xl shadow-xl"
                >
                  <Crown className="w-8 h-8 text-white" />
                </motion.div>

                <motion.div
                  animate={{ y: [0, -15, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                  className="absolute -top-6 -right-6 bg-gradient-to-br from-purple-500 to-pink-600 p-4 rounded-2xl shadow-xl"
                >
                  <Crown className="w-8 h-8 text-white" />
                </motion.div>

                {/* Center content */}
                <div className="text-center space-y-6">
                  <div className="relative inline-block">
                    <div className="absolute inset-0 bg-gradient-to-r from-rose-400 to-pink-400 rounded-full blur-2xl opacity-50"></div>
                    <div className="relative bg-gradient-to-br from-rose-100 to-pink-100 p-16 rounded-full">
                      <div className="text-7xl">👰🏻‍♀️🤵🏻‍♂️</div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-3xl font-bold text-gray-800">Your Dream Day</h3>
                    <p className="text-gray-600 max-w-sm mx-auto">
                      Step into the role of bride and groom for a day filled with tradition, love, and celebration.
                    </p>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4 pt-6">
                    {[
                      { label: 'Full Day', value: '8+hrs' },
                      { label: 'Rituals', value: '10+' },
                      { label: 'Memories', value: '∞' }
                    ].map((stat, index) => (
                      <div key={index} className="space-y-1">
                        <div className="text-2xl font-bold text-rose-600">{stat.value}</div>
                        <div className="text-xs text-gray-600 uppercase tracking-wider">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Decorative elements */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                className="absolute -bottom-8 -right-8 w-24 h-24 border-4 border-dashed border-rose-300 rounded-full"
              />
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                className="absolute -top-8 -left-8 w-16 h-16 border-4 border-dashed border-purple-300 rounded-full"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CoupleBanner;
